import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ANNUAL_MEMBERSHIP_MIN_AMOUNT,
  CARD_DARK,
  DonationFrequency,
  FONT_SANS,
  getGiftableMemberships,
  getMembershipMonths,
  PATRON_MIN_AMOUNT,
  PURPLE,
  TRIAL_DAYS,
} from "./checkoutV2.constants";

interface DonationSummaryV2Props {
  frequency: DonationFrequency;
  amount: number;
  /** Extra opted-in amount (e.g. processing fee). */
  additionalAmount?: number;
  /** Show the "Auto Renewal (12 months)" line when the option is active. */
  autoRenew?: boolean;
  /** Free-trial signup: show the membership terms instead of the donation math. */
  trialMode?: boolean;
}

const formatUsd = (value: number) =>
  `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Card shell and heading, shared by the donation and free-trial summaries. */
const cardSx = {
  backgroundColor: CARD_DARK,
  border: `2px solid ${PURPLE}`,
  borderRadius: "12px",
  p: { xs: 3, md: 4 },
};
const headingSx = {
  fontFamily: FONT_SANS,
  fontSize: { xs: "14px", md: "20px" },
  fontWeight: 600,
  lineHeight: 1.2,
  letterSpacing: "2.5px",
  textTransform: "uppercase",
  color: "common.white",
  mb: { xs: 2.5, md: 0.7 },
} as const;

const Row = ({
  label,
  value,
  bold,
  size,
  letterSpacing
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  bold?: boolean;
  size?: string;
  letterSpacing?: string;
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: {xs:"12px", md:"14px"},
        lineHeight:1,
        fontWeight: bold ? 700 : 400,
        color: "common.white",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT_SANS,
        fontSize: size ? size : "12.5px",
        fontWeight: bold ? 700 : 400,
        lineHeight:1.2,
        color: "common.white",
        letterSpacing: letterSpacing ? letterSpacing : " "
      }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * Dark "Donation Summary" card for the redesign. Mirrors the legacy summary
 * math (subtotal + extras = total) but with the new visual treatment.
 */
const DonationSummaryV2 = ({
  frequency,
  amount,
  additionalAmount = 0,
  autoRenew = false,
  trialMode = false,
}: DonationSummaryV2Props) => {
  const { t } = useTranslation();
  const isMonthly = frequency === "monthly";
  const isPatron = !isMonthly && amount >= PATRON_MIN_AMOUNT;
  const isAnnualMember =
    !isMonthly &&
    amount >= ANNUAL_MEMBERSHIP_MIN_AMOUNT &&
    amount < PATRON_MIN_AMOUNT;
  const months = getMembershipMonths(amount);
  const giftableMemberships = getGiftableMemberships(amount);
  const total = amount + additionalAmount;

  // A free trial charges nothing today, so there is no subtotal, total or term to
  // show — just the terms, in one line. This is the only place on the page the
  // price appears.
  if (trialMode) {
    return (
      <Box sx={cardSx}>
        <Typography sx={headingSx}>
          {t("donationPage.trialSummaryHeading")}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_SANS,
            fontSize: { xs: "16px", md: "18px" },
            fontWeight: 400,
            color: "common.white",
            lineHeight: 1.4,
          }}
        >
          {t("donationPage.trialSummaryLine", { days: TRIAL_DAYS, amount })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={cardSx}>
      <Typography sx={headingSx}>
        {t("donationPage.summary")}
      </Typography>

      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: "14px",
          letterSpacing: "0.5px",
          fontWeight: 400,
          color: "#FFFFFF",
          lineHeight:1.2
        }}
      >
        {isMonthly ? (
          t("donationPage.monthlyDonation")
        ) : (
          <>
            {t("donationPage.oneTimeDonation")}
            {amount < ANNUAL_MEMBERSHIP_MIN_AMOUNT &&
              ` ${t("donationPage.monthsMembership", { count: months })}`}
            {isAnnualMember && ` ${t("donationPage.annualMembershipSuffix")}`}
            {isPatron && (
              <>
                {" • "}
                <Box
                  component="span"
                >
                  {t("donationPage.patron")}
                </Box>
              </>
            )}
          </>
        )}
      </Typography>

      <Typography
        sx={{
          fontFamily: FONT_SANS,
          fontSize: "48px",
          fontWeight: 800,
          color: "common.white",
          lineHeight: "48px",
          letterSpacing: "-2px",
          mt: 0.7,
          mb:{xs:2.5, md: 1}
        }}
      >
        {`$${amount.toLocaleString("en-US")}`}
      </Typography>

      {amount === 100 && (
        <Box
          className="jg-amount-badge"
          sx={{
            backgroundColor: "#F6B60B14",
            border: "1px solid #F6B60B47",
            borderRadius: "6px",
            px: 1,
            py: "6px",
            maxWidth:"157px",
            mb:{xs:2.5, md: 1},
            textTransform: "uppercase",
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: "10px",
              fontWeight: 600,
              color: "#F6B60B",
              lineHeight: 1,
              letterSpacing: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            {` ${t("donationPage.youreSaving17")}`}
          </Typography>
        </Box>
      )}

      <Row label={t("donationPage.subtotal")} value={formatUsd(amount)} />
      {additionalAmount > 0 && (
        <Row
          label={t("donationPage.transactionFee")}
          value={formatUsd(additionalAmount)}
        />
      )}

      <Box
        sx={{
          height: "1px",
          backgroundColor: "#FFFFFF4D",
          my: {xs:2.5, md:1.4},
        }}
      />

      <Row label={t("donationPage.total")} value={`$${total.toLocaleString("en-US")}`} bold size="22px" letterSpacing="-0.5px"/>
      <Box
        sx={{
          height: "1px",
          backgroundColor: "#FFFFFF4D",
          mt: {xs:2.5, md:1.4},
        }}
      />

      {autoRenew && (
        <Typography
          sx={{
            fontFamily: FONT_SANS,
            fontSize: "13px",
            fontWeight: 400,
            color: "rgba(255,255,255,0.7)",
            mt:{xs:2.5, md:1.4},
          }}
        >
          {t("donationPage.autoRenewal12")}
        </Typography>
      )}

      {isPatron && (
        <Typography
          sx={{
            fontFamily: FONT_SANS,
            fontSize: "13px",
            fontWeight: 400,
            color: "rgba(255,255,255,0.7)",
            mt: 1.4,
            lineHeight: 1.4,
          }}
        >
          <span style={{color:"#F6B60B"}}>*</span>
          {t("donationPage.patronGiftNote", { count: giftableMemberships })}
        </Typography>
      )}
    </Box>
  );
};

export default DonationSummaryV2;
